import Navbar from "@/Components/Navbar"
import backgroundImage from "@/images/backgrounds/lobby-background.webp"
import { host } from "@/utils/utils"
import { Link } from "react-router-dom"
import { useEffect, useState } from "react"
import ModalButton from "@/Components/ModalButton"
import Swal from "sweetalert2"
import { gameRoles } from "@/utils/gameRoles"

type SavedHistoryItem = {
  gameId: string
  turnsPlayed: number
  gameEnded?: boolean
  endReason?: "networth" | "bankruptcy" | null
  winnerNetworth?: number | null
  winnerRole?: string | null
  winnerOrder?: number | null
}

const getRoleMeta = (roleId?: string | null) =>
  gameRoles.find((role) => role.id === roleId)

const GameHistory = () => {
  const [savedHistoryIds, setSavedHistoryIds] = useState<SavedHistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [deletingGameId, setDeletingGameId] = useState<string | null>(null)
  useEffect(() => {
    fetch(`${host}/api/stats/get-all-saved-history-ids`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) =>
        setSavedHistoryIds(
          data.map((item: any) => ({
            gameId: item.gameId,
            turnsPlayed: item.turnsPlayed,
            gameEnded: item.gameEnded,
            endReason: item.endReason,
            winnerNetworth: item.winnerNetworth,
            winnerRole: item.winnerRole,
            winnerOrder: item.winnerOrder,
          }))
        )
      )
      .finally(() => setLoading(false))
  }, [])

  const handleDeleteAll = async () => {
    const result = await Swal.fire({
      title: "Delete All History?",
      text: "This will permanently delete all your saved game history. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete all",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      heightAuto: false,
    })
    if (result.isConfirmed) {
      try {
        const res = await fetch(`${host}/api/stats/delete-all`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        })
        if (!res.ok) throw new Error("Failed to delete history")
        setSavedHistoryIds([])
        Swal.fire({
          title: "Deleted!",
          text: "All game history has been deleted.",
          icon: "success",
          heightAuto: false,
        })
      } catch (err) {
        Swal.fire({
          title: "Error",
          text: err instanceof Error ? err.message : "Failed to delete history.",
          icon: "error",
          heightAuto: false,
        })
      }
    }
  }

  const handleDeleteOne = async (gameId: string) => {
    const result = await Swal.fire({
      title: "Delete this game?",
      text: "This will remove it from your saved history. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      heightAuto: false,
    })
    if (!result.isConfirmed) return
    setDeletingGameId(gameId)
    try {
      const res = await fetch(
        `${host}/api/stats/saved-history/${encodeURIComponent(gameId)}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      )
      if (!res.ok) {
        if (res.status === 404) {
          setSavedHistoryIds((prev) => prev.filter((x) => x.gameId !== gameId))
          return
        }
        throw new Error("Failed to delete history")
      }
      setSavedHistoryIds((prev) => prev.filter((x) => x.gameId !== gameId))
      Swal.fire({
        title: "Deleted",
        text: "The game was removed from your history.",
        icon: "success",
        heightAuto: false,
      })
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err instanceof Error ? err.message : "Failed to delete history.",
        icon: "error",
        heightAuto: false,
      })
    } finally {
      setDeletingGameId(null)
    }
  }

  return (
    <div className="w-screen h-screen bg-cover flex flex-col" style={{ backgroundImage: `url(${backgroundImage})` }}>
      <Navbar />
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-xl text-white pl-[max(1rem,var(--safe-left))] pr-[max(1rem,var(--safe-right))]">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-4xl font-bold text-center flex-1">Game History</h2>
            {savedHistoryIds.length > 0 && (
              <ModalButton color="red" size="sm" className="ml-4" onClick={handleDeleteAll}>
                Delete All
              </ModalButton>
            )}
          </div>
          <p className="text-gray-300 mb-6 text-center text-lg">View your past games and match details below.</p>
          {!loading && savedHistoryIds.length > 0 && (
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="bg-black/60 border border-gray-700 rounded-xl px-5 py-3 text-center shadow-lg">
                <div className="text-2xl font-bold text-blue-300">
                  {savedHistoryIds.length}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">
                  Total Games
                </div>
              </div>
              <div className="bg-black/60 border border-gray-700 rounded-xl px-5 py-3 text-center shadow-lg">
                <div className="text-2xl font-bold text-emerald-400">
                  {savedHistoryIds.filter((item) => item.gameEnded).length}
                  <span className="text-gray-500">/{savedHistoryIds.length}</span>
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">
                  Completed
                </div>
              </div>
            </div>
          )}
          <div className="bg-black/60 border border-gray-700 h-[60vh] overflow-auto rounded-2xl p-8 min-h-[300px] shadow-xl">
            {loading ? (
              <div className="text-center text-xl text-gray-400 py-16">Loading...</div>
            ) : savedHistoryIds.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <span className="text-2xl text-gray-400 font-semibold">No game history found.</span>
                <span className="text-gray-500">Play some games to see your history here!</span>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {savedHistoryIds.map((item) => (
                  <div
                    key={item.gameId}
                    className="flex items-stretch gap-3 bg-gray-800/70 border border-gray-600 rounded-lg px-4 py-3 shadow-md min-w-0"
                  >
                    <Link
                      to={`/game-history/${item.gameId}`}
                      className="flex-1 flex flex-col gap-1.5 min-w-0 font-mono hover:text-white transition-all duration-150 rounded-md px-2 py-1 -m-1 hover:bg-blue-900/50"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 min-w-0 text-lg">
                        <span className="text-blue-300 truncate">{item.gameId}</span>
                        <span className="text-gray-400 sm:ml-auto shrink-0">
                          {item.turnsPlayed} turns played
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap text-sm font-sans">
                        {item.gameEnded ? (
                          <>
                            {(() => {
                              const roleMeta = getRoleMeta(item.winnerRole)
                              return (
                                <span
                                  className="px-2 py-0.5 rounded-full font-semibold"
                                  style={{
                                    backgroundColor: `${roleMeta?.color ?? "#6B7280"}33`,
                                    color: roleMeta?.color ?? "#9CA3AF",
                                  }}
                                >
                                  🏆 {roleMeta?.title ?? item.winnerRole ?? "Unknown"}
                                </span>
                              )
                            })()}
                            <span className="text-gray-400">
                              {item.endReason === "networth"
                                ? "Won by networth"
                                : "Won by bankruptcy"}
                            </span>
                            {item.endReason === "networth" &&
                              item.winnerNetworth != null && (
                                <span className="text-emerald-400 font-semibold">
                                  {item.winnerNetworth}M
                                </span>
                              )}
                          </>
                        ) : (
                          <span className="text-yellow-500/80">
                            In progress / not finished
                          </span>
                        )}
                      </div>
                    </Link>
                    <ModalButton
                      type="button"
                      color="red"
                      size="sm"
                      className="shrink-0 self-center"
                      disabled={deletingGameId === item.gameId}
                      onClick={() => handleDeleteOne(item.gameId)}
                    >
                      {deletingGameId === item.gameId ? "…" : "Delete"}
                    </ModalButton>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GameHistory
